import { DocumentInfo } from './document'
import { GridSelectionModel } from '@mui/x-data-grid'
import { InvoiceGroupInfo } from './invoiceGroup'
import { Warnings } from './common'
import { SplitFunding } from 'app/InvoiceManagement/components/SplitFundingForm'
import { LabelsInfo } from './labels'
import { FundingReleaseItemInfo } from './fundingRelease'
import { CompanyScheduledFeeInfo } from './companyScheduledFee'

/* ---------------------------------------------
                  Utils
 --------------------------------------------- */

const validInvoiceStatuses = ['PENDING', 'APPROVED_FOR_FUNDING', 'FUNDED', 'PAID', 'REJECTED', 'DELETED']
export type InvoiceStatus = 'PENDING' | 'APPROVED_FOR_FUNDING' | 'FUNDED' | 'PAID' | 'REJECTED' | 'DELETED'

export function isInvoiceStatus(val: string): val is InvoiceStatus {
  return validInvoiceStatuses.includes(val)
}

type InvoiceChangelogLogType = 'UPDATE' | 'VERIFICATION' | 'COLLECTION'

/* ---------------------------------------------
                  Responses
 --------------------------------------------- */

export type InvoiceListResponse = {
  invoices: InvoiceSummaryInfo[]
}

export type InvoiceListTotalsResponse = {
  totals: InvoiceListTotalsInfo | null
}

export type InvoiceListTotalsInfo = {
  amount: number
  balance: number
}

export type InvoiceResponse = {
  invoice: InvoiceSummaryInfo
  warnings: Warnings[]
}

export type InvoicePreferredSplit = {
  paymentType: string
  percentage: number
}

export type InvoicePreviewResponse = {
  advanceAmount: number
  cashReserve: number
  escrowReserve: number
  creditLimits: CreditLimitInfo
  feesAmount: number
  invoiceAmount: number
  status: string
  paymentFees: paymentFeesInfo
  fundingInstruction: FundingInstruction[]
  invoiceBreakdown: invoiceBreakdownInfo[]
  fuelAdvanceAmount: number
  quickPayFeeAmount: number
  vendorAdvanceAmount: number
  brokerQuickPayFeeAmount: number
  factorQuickPayFeeAmount: number
  preferredPaymentMethod?: string | null
  preferredPaymentSplit?: InvoicePreferredSplit[] | null
  companyScheduledFees: CompanyScheduledFeeInfo[]
  feeEscrow: number
}

export type InvoiceApproveForFundingResponse = {
  invoiceGroup: InvoiceGroupInfo
}

export type InvoiceCsvResponse = {
  code: number
  status: string
  output: string[]
}

export type InvoiceRebillResponse = {
  rebilledInvoiceIds: string[]
  rebilledInvoiceCount: number
  notRebilledInvoiceIds: string[]
  notRebilledInvoiceCount: number
}

export type InvoiceLineItemResponse = {
  lineItems: InvoiceLineItemInfo[]
}

export type InvoiceCollectionFeeListResponse = {
  invoiceCollectionFees: InvoiceCollectionFeeInfo[]
}

export type AppliedTransactionType = 'PAYMENT' | 'CHARGEBACK' | 'WRITE_OFF'

export type AppliedTransactionInfo = {
  type: AppliedTransactionType
  amount: number
  date: string
  reference: string | null
  status: string | null
  paymentId: string | null
  paymentStatus: string | null
  returned?: boolean
  returnedOn?: string | null
  // why returning this invoice failed
  returnError?: string | null
}

export type InvoiceAppliedTransactionListResponse = {
  appliedTransactions: AppliedTransactionInfo[]
}

/* ---------------------------------------------
                  Infos
 --------------------------------------------- */

export type InvoiceSummaryInfo = {
  id: string
  invoiceNumber: string
  referenceNumber: string
  companyClientId: string
  companyClientName: string
  companyDebtorId: string
  companyDebtorName: string
  termId: string
  termName: string
  status: string
  invoiceAmount: number
  invoiceBalance: number
  creditBalance?: number
  invoiceDate: string
  notes: string
  documents: DocumentInfo[]
  nDocuments?: number
  nActiveDocuments?: number
  verificationStatus: string
  changelog: InvoiceChangelogInfo[]
  ineligible: boolean
  disputed: boolean
  createdOn: string
  purchaseFeeAmount: number
  escrowReserveAmount: number
  cashReserveAmount: number
  advanceAmount: number
  daysAged: number
  daysOutstanding: number
  paidDate: string
  paymentStatus: string
  fundedDate: string
  closedDate: string
  callBackDate: string
  willPayDate: string
  paymentTransactionNumber: string[]
  cashEscrowAmount?: number
  fuelAdvanceId: string
  fuelAdvanceReferenceNumber: string
  fuelAdvanceAmount: number
  forcedTermId: string
  forcedTermName: string
  companyBrokerId: string
  companyBrokerName: string
  invoiceType: string
  vendorPaymentAmount: number
  vendorInvoiceId: string
  vendorAdvanceAmount: number
  quickPayFeeAmount: number
  brokerQuickPayFeeAmount: number
  factorQuickPayFeeAmount: number
  quickPayFeeId: string
  vendorPaymentDate: string
  vendorFundingDate: string
  companyVendorName: string
  companyVendorId: string
  overAdvanceType: string
  overAdvanceAmount: number
  snapshotTermPurchaseRate: number
  snapshotTermEscrowReserveRate: number
  nonFactoredInvoice: boolean | string
  recourse: boolean | null
  verificationMethod: string
  cachedCompanyName?: string
  cachedCompanyId?: string
  isOutOfSystemInvoice?: boolean //This value is not present in the API response. Is it a custom field added by the frontend.
  labels?: LabelsInfo[]
  dueDate: string | null
  updatedOn: string | null
  hasCombinedDocument: boolean
  hasBillingDocument: boolean
  billingDocumentGeneratedAt: string | null
  escrowReserveBalance?: number | null
  preferredPaymentMethod?: string | null
  preferredPaymentSplit?: InvoicePreferredSplit[] | null
  escrowFeeAmount?: number | null
  collectionStatusId?: string | null
  collectionStatusName?: string | null
  collectionStatusColour?: string | null
}

export type InvoiceChangelogList = {
  changelogs: InvoiceChangelogInfo[]
}

export type InvoiceChangelogInfo = {
  id: string
  logType: InvoiceChangelogLogType
  status: string
  method: string
  notes: string
  createdOn: Date
  userId: string | null
  userEmail: string | null
  apiKeyId: string | null
  apiKeyName: string | null
  ineligibleStatus: Boolean
  disputedStatus: Boolean
  labels: string
  collectionStatusName: string | null
}

export type CreditLimit = {
  companyId: string
  companyName: string
  noBuy?: boolean
  creditLimit: number | null
  creditLimitApproved: boolean
  currentOpenAr: number
  newApprovedAmount: number
  parentCreditLimit: number
  parentCurrentOpenAr: number
  parentName: string
  cashReserve: number
  negativeCreditLimit: boolean
  entityCompanyId: string
  entityCompanyName: string
  invoiceCount?: number
  creditLimitExpirationDate?: string | null
  creditLimitExpired?: boolean
  parentCreditLimitExpirationDate?: string | null
  parentCreditLimitExpired?: boolean
}

export type CreditLimitInfo = {
  client: CreditLimit
  broker: CreditLimit
  debtors: CreditLimit[]
  clientDebtors: CreditLimit[]
  brokerDebtors: CreditLimit[]
}

export type paymentFeesInfo = {
  achFee: number
  checkFee: number
  fuelCardFee: number
  sameDayAchFee: number
  wireFee: number
  moneyCodeFee: number
  digitalWalletFee: number
  instantFee: number
  rtpFee: number
}

export type FundingInstruction = {
  createdOn: string
  default: boolean
  favorite: boolean
  id: string
  name: string
  paymentMethod: string
  activeStatus: boolean
}

export type invoiceBreakdownInfo = {
  cashReserve: number
  escrowReserve: number
  feePerInvoice: number
  invoiceId: string
  invoiceNumber: string
  minimumPurchaseFee: number
  netFee: number
  purchaseFee: number
  termId: string
  termName: string
  quickPayFeeAmount: number
  vendorAdvanceAmount: number
  brokerQuickPayFeeAmount: number
  factorQuickPayFeeAmount: number
  fuelAdvance: FuelAdvanceInvoiceBreakdownDetails
  invoiceReference: string
  invoiceAmount: number
  invoiceBalance: number
  daysOutstanding: number
  daysAged: number
  disputed: boolean
  ineligible: boolean
  debtorDeliveryMethod: string
  debtorBillingEmails: string
  nActiveDocuments?: number
}

export type FuelAdvanceInvoiceBreakdownDetails = {
  referenceNumber: string
  fuelAdvanceAmount: number
}

export type OverAdvanceInvoiceBreakdownDetails = {
  referenceNumber: string
  overAdvanceAmount: number
}

export type InvoiceLineItemInfo = {
  id: string
  code: string
  amount: number
}

export type InvoiceCollectionFeeInfo = {
  id: string
  invoicePrincipalAmount: number
  grossFeeAmount: number
  feeIncreaseAmount: number
  ledgerAccrualAmount: number
  collectionFeeRate: number
  collectionFeeRateIncrease: number
  accrualDate: string
  collectionFeeStartDay: number
  backdated: boolean
}

/* ---------------------------------------------
                  Requests
 --------------------------------------------- */

export type InvoiceVerifyRequest = {
  invoiceIds?: string[]
  companyClientId?: string
  companyBrokerId?: string
  selectAllRequest?: boolean
  selectAllStatus?: string
  filters?: InvoiceListFilters
  verificationStatus: string | null
  verificationNotes: string | null
  verificationMethod: string | null
}

export type InvoiceCreateRequest = {
  invoiceNumber: string
  referenceNumber: string
  companyClientId?: string | null
  companyDebtorId: string
  invoiceAmount: number
  invoiceDate: Date
  notes: string
  fuelAdvanceId?: string
  forcedTermId?: string
  companyBrokerId?: string | null
  vendorInvoiceId?: string | null
  vendorPaymentAmount?: number | null
  vendorPaymentDate?: Date | null
  quickPayId?: string | null
  companyVendorId?: string | null
  overAdvanceType?: string | null
  overAdvanceAmount?: number | null
  nonFactoredInvoice?: boolean | null
  recourse?: boolean
  lineItems?: CreateInvoiceLineItemRequest[]
  preferredPaymentMethod?: string | null
  dueDate?: Date | null
}

export type InvoicePreviewRequest = {
  invoices?: GridSelectionModel | undefined | string | string[]
  transactionId?: string | undefined | null
  selectAllRequest?: boolean
  companyClientId?: string
  companyBrokerId?: string
} & InvoiceListFilters

export type InvoiceListFilters = {
  q?: string
  debtor?: string | null
  verificationStatus?: string
  accountExecutiveUserId?: string
  preferredPaymentMethod?: string | null
  brokerMod?: boolean
  nonFactoredInvoice?: boolean | null
  recourse?: boolean | null
  invoiceDateStart?: Date
  invoiceDateEnd?: Date
  fundingDateStart?: Date
  fundingDateEnd?: Date
  amountMin?: number
  amountMax?: number
  labels?: string
  startAt?: Date | null
  endAt?: Date | null
  balanceMin?: number | null
  balanceMax?: number | null
  invoiceLineItemCode?: string
  startDueDate?: Date | null
  endDueDate?: Date | null
  companyTeamId?: string
}

export type ReserveHoldReleaseItemInfo = {
  amount?: number | null
  releaseType?: string | null
  notes?: string | null
  id: string
  showNotes?: boolean
  name?: string
  companyScheduledFeeId?: string
  isCompanyScheduledFee?: boolean
}

export type AdditionalFeeItemInfo = {
  amount?: number | null
  feeType?: 'CHARGE' | 'CREDIT'
  notes?: string | null
  id: string
  showNotes?: boolean
  name?: string
  companyScheduledFeeId?: string
  isCompanyScheduledFee?: boolean
  glCode?: string
}

type FundingChargebackItem = {
  id: string
  amount: number
}

export type InvoiceApproveForFundingRequest = {
  companyClientId?: string
  companyBrokerId?: string
  fundingInstructionId?: string | null
  invoiceIds?: string[]
  splitFunding?: SplitFunding[] | null
  pendingTransactions?: PendingFundingInfo
  zeroOutPaymentFee?: boolean
  fundingChargebacks?: FundingChargebackItem[]
  fuelAdvanceFundingChargebacks?: FundingChargebackItem[]
  overAdvanceFundingChargebacks?: FundingChargebackItem[]
  fundingCredits?: string[]
  account?: string
}

export type OpenCreditInfo = {
  id: string
  invoiceNumber: string
  creditBalance: number
}

export type PendingFundingInfo = {
  holdFeeAmount: number
  feeNotes?: string
  holdCashReserveAmount?: number
  cashReserveNotes?: string
  cashReserveItems?: FundingReleaseItemInfo[]
  feeItems?: FundingReleaseItemInfo[]
}

export type UpdateInvoiceRequest = {
  companyClientId?: string
  companyDebtorId?: string
  invoiceNumber?: number
  referenceNumber?: number
  invoiceAmount?: number
  invoiceDate?: string
  notes?: string
  documents?: string[]
  status?: string
  fuelAdvanceId?: string
  forcedTermId?: string | null
  companyVendorId?: string
  vendorPaymentDate?: Date
  vendorPaymentAmount?: number
  vendorInvoiceId?: string
  quickPayId?: string
  companyBrokerId?: string
  overAdvanceType?: string
  overAdvanceAmount?: number
  nonFactored?: boolean | null
  recourse?: boolean
  dueDate?: Date | null
}

export type RejectAllInvoicesRequest = {
  companyClientId?: string
  companyBrokerId?: string
  invoiceStatus?: string
}

export type InvoiceChangelogParams = {
  invoiceId?: string
  q?: string
  category?: string
}

export type ExportInvoicesParamsRequest = {
  q?: string
  status?: string
  client?: string | null
  broker?: string | null
  debtor?: string | null
  verificationStatus?: string | null
  sort?: string
  accountExecutiveUserId?: string | null
  brokerMod?: boolean
  nonFactoredInvoice?: boolean | null
  recourse?: boolean | null
  invoiceDateStart?: Date | null
  invoiceDateEnd?: Date | null
  fundingDateStart?: Date | null
  fundingDateEnd?: Date | null
  amountMin?: number | null
  amountMax?: number | null
  labels?: string | null
  skipPagination?: boolean
  exportType?: string
  startAt?: Date | null
  endAt?: Date | null
  balanceMin?: number | null
  balanceMax?: number | null
  hideCollectionUpdatedToday?: boolean
  callBackDate?: Date | null
  willPayDate?: Date | null
  ineligible?: boolean | null
  disputed?: boolean | null
  invoiceGroupId?: string
  onlyZeroBalance?: boolean | null | undefined
  screen?: string
  collectionAgentUserId?: string | null
  daysOutstandingMin?: number | null
  daysOutstandingMax?: number | null
  daysAgedMin?: number | null
  daysAgedMax?: number | null
  invoiceLineItemCode?: string | null
  preferredPaymentMethod?: string | null
  startDueDate?: Date | null
  endDueDate?: Date | null
}

export type RejectedInvoiceRequest = {
  invoiceId: string
  payload: UpdateInvoiceRequest
}

export type DeletedInvoiceRequest = {
  invoiceId: string
}

export type InvoiceDocAmountSource = 'AMOUNT' | 'BALANCE'

export type InvoicesRebillRequest = {
  invoiceIds: string[]
  regenerateInvoiceDocuments: boolean
  invoiceDocAmountSourceByInvoiceId?: Record<string, InvoiceDocAmountSource>
}

export type CreateInvoiceLineItemRequest = {
  code?: string
  amount?: number
}

export type DeletedInvoiceLineItemRequest = {
  invoiceId: string
  lineItemId: string
}

export type UpdateInvoiceLineItemRequest = {
  code?: string
  amount?: number
}

/* ---------------------------------------------
                  List Params
 --------------------------------------------- */

export type InvoiceListParams = {
  q?: string
  status?: string
  client?: string | null
  broker?: string | null
  debtor?: string | null
  verificationStatus?: string | undefined | null
  hideCollectionUpdatedToday?: boolean
  callBackDate?: Date | null
  willPayDate?: Date | null
  ineligible?: boolean | null
  disputed?: boolean | null
  sort?: string
  invoiceGroupId?: string
  onlyZeroBalance?: boolean | null | undefined
  withCredits?: boolean
  accountExecutiveUserId?: string | null
  brokerMod?: boolean
  nonFactoredInvoice?: boolean | null
  recourse?: boolean | null
  screen?: string
  invoiceDateStart?: Date | null
  invoiceDateEnd?: Date | null
  fundingDateStart?: Date | null
  fundingDateEnd?: Date | null
  amountMin?: number | null
  amountMax?: number | null
  labels?: string | null
  startAt?: Date | null
  endAt?: Date | null
  balanceMin?: number | null
  balanceMax?: number | null
  collectionAgentUserId?: string | null
  daysOutstandingMin?: number | null
  daysOutstandingMax?: number | null
  daysAgedMin?: number | null
  daysAgedMax?: number | null
  invoiceLineItemCode?: string | null
  startDueDate?: Date | null
  endDueDate?: Date | null
  queryVersion?: string | null
  preferredPaymentMethod?: string | null
}

export type PaymentInfoListParams = {
  companyClientId?: string
}

export type InvoiceLabelCollectionResponse = {
  labels: LabelsInfo[]
}

export type InvoiceLineItemParams = {
  q?: string
}

export type InvoiceCollectionFeeListParams = {
  sort?: string
  beginStartDay?: number
  endStartDay?: number
  startAccrualDate?: Date | null
  endAccrualDate?: Date | null
}

/* ---------------------------------------------
                  Props
 --------------------------------------------- */

export type SectionInformationProps = {
  invoice?: InvoiceSummaryInfo
  title: string
}

export type SectionInformationBrokerProps = {
  invoice?: InvoiceSummaryInfo
  canWriteBrokers: boolean
  title: string
}

export type InvoiceBlandAIVerifyRequest = {
  invoiceIds: string[]
}

export type InvoiceBlandAIVerifyResponse = {
  status: 'SUCCESS' | 'ERROR'
  code: number
  message: string
  results: InvoiceBlandAICallResult[]
}

export type InvoiceBlandAICallResult = {
  invoiceId: string
  callId?: string
  status: 'SUCCESS' | 'FAILED'
  errorMessage?: string
}

export type ValidateInvoiceNumbersParams = {
  invoiceNumber?: string
  referenceNumber?: string
  companyClientId?: string
  companyBrokerId?: string
  companyDebtorId?: string
}

export type ValidateInvoiceNumbersResponse = {
  invoiceNumberExists: boolean
  referenceNumberExists: boolean
  allowDuplicateReferenceNumberEnabled: boolean
  existingInvoiceIdForInvoiceNumber?: string
  existingInvoiceStatusForInvoiceNumber?: string
  existingInvoiceIdForReferenceNumber?: string
  existingInvoiceStatusForReferenceNumber?: string
  existingInvoiceNumbers: string[]
}
